import { useEffect, useState } from "react";
import type { CityFeature, CityOption } from "components/ListPage/filters";
import { fetchAutocompleteAddress } from "hooks/autocomplete";
import type { HttpResponse } from "lib/fetch";
import type { CityAutocompleteFilter } from "./types";

export const useCityAutocompleteFilter = (selectedCity: string): CityAutocompleteFilter => {
    const [cityInput, setCityInput] = useState(selectedCity);
    const [cityOptions, setCityOptions] = useState<CityOption[]>([]);

    useEffect(() => {
        setCityInput(selectedCity);
    }, [selectedCity]);

    useEffect(() => {
        if (cityInput.trim().length < 3 || cityInput === selectedCity) {
            setCityOptions([]);
            return;
        }

        let request: CancellablePromise<HttpResponse<unknown>> | undefined;
        const timeout = setTimeout(() => {
            request = fetchAutocompleteAddress(
                {
                    q: cityInput,
                    limit: 8,
                },
                "municipality"
            );

            request.then((response) => {
                const features = ((response.data as { features?: CityFeature[] }).features ?? []);
                const uniqueOptions = new Map<string, CityOption>();

                features.forEach((feature) => {
                    const city = feature.properties.city || feature.properties.label;
                    const [lon, lat] = feature.geometry.coordinates;
                    const label = feature.properties.context
                        ? `${feature.properties.label} - ${feature.properties.context}`
                        : feature.properties.label;
                    uniqueOptions.set(`${city}-${lat}-${lon}`, {
                        value: city,
                        label,
                        lat,
                        lon,
                    });
                });

                setCityOptions(Array.from(uniqueOptions.values()));
            });
        }, 300);

        return () => {
            clearTimeout(timeout);
            request?.cancel();
        };
    }, [cityInput, selectedCity]);

    return {
        cityInput,
        cityOptions,
        setCityInput,
        setCityOptions,
    };
};
